import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { AlertCircle, BotOff, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { RecentlyPlacedOrder } from '@/lib/draft-order-client';

interface StartOrderDialogProps {
  open: boolean;
  /** The AI is already paused on this conversation, so starting one pauses nothing new. */
  alreadyPaused: boolean;
  /** Branch the order will be placed for, when one is settled. Named so it is never a surprise. */
  branchName?: string;
  recentOrder?: RecentlyPlacedOrder;
  submitting?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * The disclaimer in front of taking an order by hand.
 *
 * Starting one pauses the conversation's AI, and that is not a detail: the agent stops answering this
 * customer until someone hands it back. It is also the only safe arrangement — two writers on one cart
 * is exactly what the takeover exists to prevent — so the dialog explains the consequence rather than
 * offering a choice about it.
 *
 * Nothing is sent to the customer here. Saying so is worth the line: an operator who thinks starting
 * an order messages the customer will avoid it mid-conversation, which is precisely when they need it.
 */
export function StartOrderDialog({
  open,
  alreadyPaused,
  branchName,
  recentOrder,
  submitting,
  onCancel,
  onConfirm,
}: StartOrderDialogProps) {
  const { t } = useTranslation('conversations');

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('cart.start.dialog.title')}</DialogTitle>
          <DialogDescription>{t('cart.start.dialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300">
            <BotOff className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <div className="space-y-0.5">
              <p className="font-medium">
                {alreadyPaused ? t('cart.start.dialog.aiAlreadyPaused') : t('cart.start.dialog.aiWillPause')}
              </p>
              <p>{t('cart.start.dialog.aiPauseHint')}</p>
            </div>
          </div>

          {recentOrder && <RecentOrderNotice order={recentOrder} />}

          <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
            <li>{t('cart.start.dialog.silent')}</li>
            <li>{t('cart.start.dialog.prefill')}</li>
            {branchName && <li>{t('cart.start.dialog.branch', { name: branchName })}</li>}
          </ul>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
            {t('cart.actions.cancel')}
          </Button>
          <Button type="button" loading={submitting} onClick={onConfirm}>
            {!submitting && <ShoppingCart className="h-3.5 w-3.5" />}
            {alreadyPaused ? t('cart.start.dialog.confirm') : t('cart.start.dialog.confirmWithPause')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * An order placed minutes ago that nobody has dealt with yet. A warning, never a gate: the operator
 * usually has the customer on the phone adding something they forgot, which is a perfectly good reason
 * to open a second cart — and they know that better than this rule does.
 */
export function RecentOrderNotice({ order }: { order: RecentlyPlacedOrder }) {
  const { t } = useTranslation('conversations');
  // The clock time rather than "N minutes ago": the notice sits in a panel that stays open, and a
  // relative figure would either go stale or have to re-render on a timer to stay honest.
  const time = format(new Date(order.placedAt), 'HH:mm');

  return (
    <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700 dark:border-blue-800/60 dark:bg-blue-950/40 dark:text-blue-300">
      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <div className="space-y-0.5">
        <p className="font-medium">
          {t(order.status === 'pending_payment' ? 'cart.start.recent.awaitingPayment' : 'cart.start.recent.awaiting', {
            id: order.orderId.slice(0, 8),
            time,
          })}
        </p>
        <p>{t('cart.start.recent.hint')}</p>
      </div>
    </div>
  );
}
